import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { getCurrentPosition, isNative, openAppSettings } from '@/native/permissions';

/**
 * React hook for the "Use My Location" pattern. Components call
 * `requestLocation()` only when the user explicitly opts in (e.g., taps a
 * "Near Me" button) — this is the App-Review-safe permission timing.
 *
 * Exposes:
 *   - coords:    {lat, lng} | null
 *   - state:     'idle' | 'requesting' | 'granted' | 'denied' | 'error'
 *   - requestLocation():  triggers the permission prompt + capture
 *   - clearLocation():    drops the coords (back to idle)
 *
 * Permission-denied UX (built-in):
 *   - Shows a sonner toast with an "Open Settings" action button when on
 *     native, or with explanatory text on web (browser settings are
 *     unreachable programmatically). Re-tapping the button on native deep-
 *     links into the OS settings page so the user can flip the toggle.
 */
export function useUserLocation() {
  const [coords, setCoords] = useState(null);
  const [state, setState] = useState('idle');

  const requestLocation = useCallback(async () => {
    if (state === 'requesting') return null;
    setState('requesting');

    const result = await getCurrentPosition();

    if (result.error) {
      setState(result.error === 'denied' ? 'denied' : 'error');

      if (result.error === 'denied') {
        if (isNative()) {
          toast.error('Location is turned off for TheUnityBlaze.', {
            description: 'Open Settings to enable it, then try again.',
            action: { label: 'Open Settings', onClick: () => openAppSettings() },
          });
        } else {
          toast.error('Location permission was denied.', {
            description:
              "Tap the lock icon in your browser's address bar to re-enable location for this site.",
          });
        }
      } else if (result.error === 'timeout') {
        toast.error('Location request timed out. Please try again.');
      } else if (result.error === 'unsupported') {
        toast.error('Your browser does not support location.');
      } else {
        toast.error('Could not determine your location.');
      }
      return null;
    }

    setCoords(result.coords);
    setState('granted');
    return result.coords;
  }, [state]);

  const clearLocation = useCallback(() => {
    setCoords(null);
    setState('idle');
  }, []);

  return { coords, state, requestLocation, clearLocation };
}
